import React from 'react';
import { X, ExternalLink } from 'lucide-react';

interface UniquenessDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  percentage: number;
  matches: Record<string, string>;
  ignored: string[];
  reportUrl?: string | null;
}

export function UniquenessDetailsModal({
  isOpen,
  onClose,
  percentage,
  matches,
  ignored,
  reportUrl
}: UniquenessDetailsModalProps) {
  if (!isOpen) return null;

  // Filter out ignored URLs from matches
  const filteredMatches = Object.entries(matches).filter(([url]) => {
    // Check if this URL should be ignored
    return !ignored.some(ignoredUrl => {
      try {
        const matchHostname = new URL(url).hostname.replace('www.', '');
        const ignoredHostname = new URL(ignoredUrl).hostname.replace('www.', '');
        return matchHostname === ignoredHostname;
      } catch {
        return url === ignoredUrl;
      }
    });
  });

  // Sort matches by percentage (highest first)
  const sortedMatches = filteredMatches.sort((a, b) => {
    const percentA = parseInt(a[1].replace('%', '').replace('<', '').trim());
    const percentB = parseInt(b[1].replace('%', '').replace('<', '').trim());
    return percentB - percentA;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Text Uniqueness Details
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Overall uniqueness: <span className="font-semibold text-green-600 dark:text-green-400">{percentage}%</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* CandyContent Report Link */}
          {reportUrl && (
            <div className="mb-6">
              <a
                href={reportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
              >
                <ExternalLink className="w-4 h-4" />
                View Full Report on CandyContent
              </a>
            </div>
          )}

          {/* Ignored Links */}
          {ignored.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Excluded from Analysis
              </h3>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  The following URLs were excluded from the uniqueness check:
                </p>
                <ul className="space-y-1">
                  {Array.from(new Set(ignored)).map((url, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      • {url}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Matching Sites */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Matching Content Found ({sortedMatches.length} sites)
            </h3>
            
            {sortedMatches.length === 0 ? (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 text-center">
                <p className="text-green-700 dark:text-green-400 font-medium">
                  No matches found! Your content is 100% unique.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedMatches.map(([url, matchPercentage], index) => {
                  const percent = parseInt(matchPercentage.replace('%', ''));
                  const isHighMatch = percent >= 20;
                  const isMediumMatch = percent >= 10 && percent < 20;
                  
                  return (
                    <div
                      key={index}
                      className={`rounded-lg p-4 border ${
                        isHighMatch
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                          : isMediumMatch
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                          : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                        >
                          {url}
                        </a>
                        <span
                          className={`flex-shrink-0 font-semibold text-lg ${
                            isHighMatch
                              ? 'text-red-700 dark:text-red-400'
                              : isMediumMatch
                              ? 'text-yellow-700 dark:text-yellow-400'
                              : 'text-gray-700 dark:text-gray-400'
                          }`}
                        >
                          {matchPercentage}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
